import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Download, Loader2, ArrowLeft, Users, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LW_DS_CLASS } from '@/components/lw';
import { ReportDateRangePicker } from './date-range/ReportDateRangePicker';
import { makePreset, type ReportPeriod } from './date-range/reportPeriod';
import { getMyCommissionReport, getCommissionReportAll } from '@/services/myCommissionService';
import { getRecaptureAccount, getRecaptureWorkspace, RECAPTURE_CHANGED_EVENT,
  type RecaptureAccount, type RecaptureRecord } from '@/services/recaptureService';
import { buildRoster, commissionReportUsersWithRecaptures, computeCommissionTotalsWithRecaptures, computeRosterTotals, profitabilityReconciliation,
  planBasisLabel, planBasisLabelForUser, type ReportUser, type MyCommissionReport, type AdminCommissionReport } from './myCommissionReportModel';
import { CommissionRosterTable } from './CommissionRosterTable';
import { AddProfitabilityBonusButton, ProfitabilityBonuses, ProfitabilityBonusDialog, type BonusEditorState } from './ProfitabilityBonuses';
import { CommissionPageShell, CommissionSummaryCards, CommissionAdminLedger, CommissionReportFooter } from './MyCommissionsReportView';
import { CommissionOfficerPanels } from './CommissionDetailsDrawer';
import { CommissionRecordsTable as EarningsTable } from './CommissionRecordsTable';
import { CommissionRecordPreferences } from './CommissionRecordPreferences';
import { downloadCommissionWorkbook, periodFile } from './commissionWorkbook';
import { useCommissionRosterTrend } from './useCommissionRosterTrend';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
export { EarningDetail } from './CommissionEarningTieOut';

const log = createComponentLogger('MyCommissionsReport', 'reports');

// ── Self view ──
function SelfCommissionsView() {
  const { user } = useAuth();
  const { version: refreshVersion, refresh } = useCommissionRefresh();
  const [period, setPeriod] = useState<ReportPeriod>(() => makePreset('ytd'));
  const [loadedPeriod, setLoadedPeriod] = useState(period);
  const [report, setReport] = useState<MyCommissionReport>(
    { plan: null, earnings: [], context: [] }
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<RecaptureAccount | null>(null);
  const [recaptureError, setRecaptureError] = useState(false);
  const reportFocusRef = useRef<HTMLHeadingElement>(null);
  const loaded = useRef(false);
  const requestGeneration = useRef(0);

  useEffect(() => {
    const requests = requestGeneration;
    const request = ++requests.current;
    if (!loaded.current) {
      setIsLoading(true);
    }
    setIsRefreshing(true);
    setError(null);
    void Promise.allSettled([
      getMyCommissionReport(format(period.from, 'yyyy-MM-dd'), format(period.to, 'yyyy-MM-dd')),
      user?.id ? getRecaptureAccount(user.id) : Promise.resolve(null)
    ]).then(([commissions, recapture]) => {
      if (request === requests.current) {
        if (commissions.status === 'fulfilled') {
          setReport(commissions.value);
          setLoadedPeriod(period);
          loaded.current = true;
        } else {
          log.error('Failed to load commissions', {}, commissions.reason as Error);
          setError('Could not load your commissions. Please try again.');
        }
        if (recapture.status === 'fulfilled') {
          setAccount(recapture.value);
        }
        setRecaptureError(recapture.status === 'rejected');
        setIsLoading(false);
        setIsRefreshing(false);
      }
    });
    return () => {
      requests.current++;
    };
  }, [period, refreshVersion, user?.id]);

  const recaptureRecords = useMemo(() => recaptureError ? null : account?.records ?? [], [recaptureError, account]);
  const totals = computeCommissionTotalsWithRecaptures(report.earnings, recaptureRecords, loadedPeriod, report.bonuses, report.volume_bonus_activity);
  const reconciliation = profitabilityReconciliation(report.earnings, recaptureRecords, loadedPeriod, user?.id);
  const basis = useMemo(() => planBasisLabel(report.plan), [report.plan]);

  const handleExport = useCallback(
    () =>
      downloadCommissionWorkbook({
        filename: periodFile(loadedPeriod, 'my-commissions'),
        period: loadedPeriod,
        earnings: report.earnings,
        recaptureRecords,
        recipientId: user?.id,
        bonuses: report.bonuses,
        volumeBonusActivity: report.volume_bonus_activity,
        // LOs must not see overrides paid out of their production — no splits.
        context: [],
        basisLabel: basis.primary,
        totals,
        maskResetPricingEvidence: true
      }),
    [loadedPeriod, report.earnings, report.bonuses, report.volume_bonus_activity, basis.primary, totals, recaptureRecords, user?.id]
  );

  return (
    <CommissionPageShell>
      <header className="mc-header">
        <div>
          <div className="mc-title">
            <p className="mc-eyebrow">Compensation</p><h1 ref={reportFocusRef} tabIndex={-1}>
              My Commissions
            </h1>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Badge
              variant="outline"
              className={basis.hasPlan ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-muted text-muted-foreground'}
            >
              {basis.primary}
            </Badge>
            {!basis.hasPlan && <span className="text-xs text-muted-foreground">paid custom per deal</span>}
          </div>
        </div>
        <div className="mc-header-actions">
          <ReportDateRangePicker className="mc-period" value={period} onChange={setPeriod} max={new Date()} />
          <Button className="mc-export" variant="outline" size="sm" onClick={handleExport} disabled={!loaded.current || isRefreshing || (report.earnings.length === 0 && !account?.records.length && !report.bonuses?.length && !report.volume_bonus_activity?.length)}>
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </header>

      {isLoading ? <div className="flex items-center justify-center py-16" role="status" aria-label="Loading commissions"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : <>
      <CommissionSummaryCards totals={totals} />
      {reconciliation && <p className="text-sm text-muted-foreground">{reconciliation}</p>}
      {user?.id && <CommissionOfficerPanels key={user.id} recipientId={user.id} account={account} recaptureError={recaptureError}
        onRetry={refresh} bonuses={report.bonuses ?? []} period={loadedPeriod} fallbackFocusRef={reportFocusRef} />}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <Info className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="mc-record-region">
        <EarningsTable earnings={report.earnings} context={report.context} maskResetPricingEvidence recaptureRecords={recaptureRecords} recipientId={user?.id} />
      </div>
      </>}
      <CommissionReportFooter />
    </CommissionPageShell>
  );
}

// ── Admin view (all records + per-user selector) ──
function AdminCommissionsView() {
  const { version: refreshVersion } = useCommissionRefresh();
  const [period, setPeriod] = useState<ReportPeriod>(() => makePreset('ytd'));
  const [loadedPeriod, setLoadedPeriod] = useState(period);
  const [data, setData] = useState<AdminCommissionReport>(
    { report_users: [], earnings: [], context: [] }
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [bonusEditor, setBonusEditor] = useState<BonusEditorState | null>(null);
  const [bonusMessage, setBonusMessage] = useState('');
  const bonusFocusRef = useRef<HTMLHeadingElement>(null);
  const [tab, setTab] = useState('summary');
  const [recaptureRecords, setRecaptureRecords] = useState<RecaptureRecord[] | null>([]);
  const loaded = useRef(false);
  const requestGeneration = useRef(0);

  const openBonusEditor = (editor: BonusEditorState) => {
    setBonusMessage('');
    setBonusEditor(editor);
  };

  useEffect(() => {
    setBonusMessage('');
  }, [period, selectedUserId]);

  useEffect(() => {
    const requests = requestGeneration;
    const request = ++requests.current;
    if (!loaded.current) {
      setIsLoading(true);
    }
    setIsRefreshing(true);
    setError(null);
    void Promise.allSettled([
      getCommissionReportAll(format(period.from, 'yyyy-MM-dd'), format(period.to, 'yyyy-MM-dd')),
      getRecaptureWorkspace()
    ]).then(([commissions, recapture]) => {
      if (request === requests.current) {
        if (commissions.status === 'fulfilled') {
          setData(commissions.value);
          setLoadedPeriod(period);
          loaded.current = true;
        } else {
          log.error('Failed to load all commissions', {}, commissions.reason as Error);
          setBonusMessage('');
          setError('Could not load commissions. Please try again.');
        }
        setRecaptureRecords(recapture.status === 'fulfilled' ? recapture.value.records : null);
        setIsLoading(false);
        setIsRefreshing(false);
      }
    });
    return () => {
      requests.current++;
    };
  }, [period, refreshVersion]);

  const trend = useCommissionRosterTrend({ report: data, period: loadedPeriod, records: recaptureRecords,
    ready: loaded.current && period === loadedPeriod && !isRefreshing && !error });

  const reportUsers = useMemo(
    () => commissionReportUsersWithRecaptures(data.report_users, recaptureRecords, loadedPeriod),
    [data.report_users, recaptureRecords, loadedPeriod]
  );
  const usersById = useMemo(() => {
    const m = new Map<string, ReportUser>();
    for (const u of reportUsers) {
      m.set(u.user_id, u);
    }
    return m;
  }, [reportUsers]);

  const recipientNameFor = useCallback(
    (uid: string | null | undefined) => (uid ? usersById.get(uid)?.name ?? '—' : '—'),
    [usersById]
  );

  const roster = useMemo(() => buildRoster(data.report_users, data.earnings, { records: recaptureRecords, period: loadedPeriod, bonuses: data.bonuses, volumeBonusActivity: data.volume_bonus_activity }), [data.report_users, data.earnings, data.bonuses, data.volume_bonus_activity, recaptureRecords, loadedPeriod]);
  const rosterTotals = useMemo(() => computeRosterTotals(roster), [roster]);
  const allRecordTotals = useMemo(() => computeCommissionTotalsWithRecaptures(data.earnings, recaptureRecords, loadedPeriod, data.bonuses, data.volume_bonus_activity), [data.earnings, data.bonuses, data.volume_bonus_activity, recaptureRecords, loadedPeriod]);

  const adminCardTotals = { ...allRecordTotals, totalEarned: rosterTotals.earned, totalPaid: rosterTotals.paid,
    outstanding: rosterTotals.owed, loProfitability: rosterTotals.loProfitability, profitabilityRecaptured: rosterTotals.profitabilityRecaptured };

  const selectedUser = selectedUserId ? usersById.get(selectedUserId) ?? null : null;
  const userEarnings = useMemo(
    () => (selectedUserId ? data.earnings.filter((e) => e.lo_user_id === selectedUserId) : []),
    [selectedUserId, data.earnings]
  );
  const userRecaptureRecords = useMemo(() => recaptureRecords?.filter(record => record.recipient_id === selectedUserId) ?? null, [recaptureRecords, selectedUserId]);
  const userBonuses = useMemo(() => data.bonuses?.filter(bonus => bonus.recipient_id === selectedUserId) ?? [], [data.bonuses, selectedUserId]);
  const userVolumeActivity = useMemo(() => data.volume_bonus_activity?.filter(event => event.recipient_id === selectedUserId) ?? [], [data.volume_bonus_activity, selectedUserId]);
  const userTotals = useMemo(() => computeCommissionTotalsWithRecaptures(userEarnings, userRecaptureRecords, loadedPeriod, userBonuses, userVolumeActivity), [userEarnings, userRecaptureRecords, loadedPeriod, userBonuses, userVolumeActivity]);
  const userReconciliation = profitabilityReconciliation(userEarnings, userRecaptureRecords, loadedPeriod, selectedUserId ?? undefined);
  const allReconciliation = profitabilityReconciliation(data.earnings, recaptureRecords, loadedPeriod);
  const userBasis = useMemo(() => planBasisLabelForUser(selectedUser), [selectedUser]);

  const handleExport = useCallback(() => {
    if (selectedUserId) {
      return downloadCommissionWorkbook({
        filename: periodFile(loadedPeriod, `commissions-${selectedUser?.name?.replace(/\s+/g, '-') ?? selectedUserId}`),
        period: loadedPeriod,
        earnings: userEarnings,
        recaptureRecords: userRecaptureRecords,
        recipientId: selectedUserId,
        bonuses: userBonuses,
        volumeBonusActivity: userVolumeActivity,
        // Selected-user export mirrors the user's own self export — no splits.
        context: [],
        basisLabel: userBasis.primary,
        totals: userTotals,
        maskResetPricingEvidence: true
      });
    }
    return downloadCommissionWorkbook({
      filename: periodFile(loadedPeriod, 'all-commissions'),
      period: loadedPeriod,
      earnings: data.earnings,
      recaptureRecords,
      bonuses: data.bonuses,
      volumeBonusActivity: data.volume_bonus_activity,
      context: data.context,
      participants: true,
      recipientNameFor,
      rosterRows: roster
    });
  }, [selectedUserId, selectedUser, loadedPeriod, userEarnings, userBonuses, userVolumeActivity, userTotals, userBasis.primary, data.earnings, data.bonuses, data.volume_bonus_activity, data.context, recipientNameFor, roster, recaptureRecords, userRecaptureRecords]);

  return (
    <CommissionPageShell>
      {/* Header */}
      <header className="mc-header">
        <div>
          <div className="mc-title">
            <p className="mc-eyebrow">Compensation</p><h1 ref={bonusFocusRef} tabIndex={-1}>
              Commissions
            </h1>
          </div>
          {selectedUser ? (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" className="h-6 px-1 text-muted-foreground" onClick={() => setSelectedUserId(null)}>
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                All commission users
              </Button>
              <span className="text-sm font-medium">Viewing: {selectedUser.name ?? '—'}</span>
              <Badge
                variant="outline"
                className={userBasis.hasPlan ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-muted text-muted-foreground'}
              >
                {userBasis.primary}
              </Badge>
            </div>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              Admin view — all commission users
            </div>
          )}
        </div>
        <div className="mc-header-actions">
          <ReportDateRangePicker className="mc-period" value={period} onChange={setPeriod} max={new Date()} />
          {data.bonus_owner && <AddProfitabilityBonusButton disabled={!data.bonus_entry_enabled || !data.bonus_current_month || isRefreshing} onOpen={openBonusEditor} />}
          <Select
            value={selectedUserId ?? 'all'}
            onValueChange={(v) => setSelectedUserId(v === 'all' ? null : v)}
          >
            <SelectTrigger className="w-56" aria-label="Commission user">
              <SelectValue placeholder="All commission users" />
            </SelectTrigger>
            <SelectContent className={`${LW_DS_CLASS} my-commissions mc-menu`}>
              <SelectItem value="all">All commission users</SelectItem>
              {reportUsers.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  {u.name ?? u.email ?? u.user_id}
                  {u.employment_status && u.employment_status !== 'active' ? ` (${u.employment_status})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="mc-export"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!loaded.current || isRefreshing || (selectedUserId ? userEarnings.length === 0 && !userRecaptureRecords?.length && !userBonuses.length && !userVolumeActivity.length : reportUsers.length === 0)}
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </header>

      {bonusMessage && <p role="status" className="text-sm text-muted-foreground">{bonusMessage}</p>}
      {data.bonus_owner && !data.bonus_entry_enabled && <p className="text-xs text-muted-foreground">Bonus entry is disabled pending activation. Existing credits remain effective; Anthony can still void them.</p>}
      {bonusEditor && data.bonus_current_month && <ProfitabilityBonusDialog editor={bonusEditor} users={data.report_users}
        selectedUserId={selectedUserId} currentMonth={data.bonus_current_month} period={loadedPeriod} fallbackFocusRef={bonusFocusRef}
        onClose={() => setBonusEditor(null)} onSaved={setBonusMessage} />}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <Info className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : selectedUserId ? (
        // Single-user detail
        <>
          <CommissionSummaryCards totals={userTotals} />
          {userReconciliation && <p className="text-sm text-muted-foreground">{userReconciliation}</p>}
          <CommissionOfficerPanels key={selectedUserId} recipientId={selectedUserId} bonuses={userBonuses} period={loadedPeriod} users={data.report_users} showHistory
            fallbackFocusRef={bonusFocusRef}
            allowEmail={data.bonus_owner && !isRefreshing}
            onEdit={data.bonus_owner && data.bonus_entry_enabled && !isRefreshing ? (bonus, trigger) => openBonusEditor({ bonus, trigger }) : undefined}
            onVoid={data.bonus_owner && !isRefreshing ? (bonus, trigger) => openBonusEditor({ bonus, trigger, voiding: true }) : undefined} />
          <div className="mc-record-region">
              {/* Selected-user drill-in mirrors the user's OWN self view — no
                  splits (overrides paid out are not shown to/for that user). */}
              <EarningsTable earnings={userEarnings} context={[]} maskResetPricingEvidence recaptureRecords={userRecaptureRecords} recipientId={selectedUserId} />
          </div>
        </>
      ) : (
        // All officers — Summary | Records | Bonuses
        <div className="mc-admin-view">
          {tab !== 'bonuses' && <><CommissionSummaryCards totals={adminCardTotals} users={roster.length} /><CommissionAdminLedger totals={allRecordTotals} reconciliation={allReconciliation} /></>}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList aria-label="Commission views" className="h-auto flex-wrap">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="records">Records ({allRecordTotals.loanCount})</TabsTrigger>
            <TabsTrigger value="bonuses">Bonuses ({data.bonuses?.length ?? 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-4 space-y-4">
            <div className="mc-record-region">
                <CommissionRosterTable roster={roster} earnings={data.earnings} users={reportUsers} trend={trend} onSelect={(uid) => setSelectedUserId(uid)} />
            </div>
          </TabsContent>

          <TabsContent value="records" className="mt-4 space-y-4">
            <div className="mc-record-region">
                <EarningsTable
                  earnings={data.earnings}
                  context={data.context}
                  showParticipants
                  showSplits
                  recipientNameFor={recipientNameFor}
                  recaptureRecords={recaptureRecords}
                />
            </div>
          </TabsContent>
          <TabsContent value="bonuses" className="mt-4 min-w-0">
            <ProfitabilityBonuses bonuses={data.bonuses ?? []} period={loadedPeriod} users={data.report_users} showHistory
              allowEmail={data.bonus_owner && !isRefreshing}
              onEdit={data.bonus_owner && data.bonus_entry_enabled && !isRefreshing ? (bonus, trigger) => openBonusEditor({ bonus, trigger }) : undefined}
              onVoid={data.bonus_owner && !isRefreshing ? (bonus, trigger) => openBonusEditor({ bonus, trigger, voiding: true }) : undefined} />
          </TabsContent>
        </Tabs></div>
      )}
      <CommissionReportFooter showSplits={!selectedUserId && tab === 'records'} />
    </CommissionPageShell>
  );
}

function useCommissionRefresh() {
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion(value => value + 1), []);
  useEffect(() => {
    window.addEventListener(RECAPTURE_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(RECAPTURE_CHANGED_EVENT, refresh);
    };
  }, [refresh]);
  return { version, refresh };
}

export function MyCommissionsReport() {
  const { user } = useAuth();
  const roles = (user as { roles?: string[] } | null)?.roles ?? [];
  const isAdmin = roles.some((r) => r === 'admin' || r === 'super_admin');
  return <CommissionRecordPreferences key={user?.id}>
    {isAdmin ? <AdminCommissionsView /> : <SelfCommissionsView />}
  </CommissionRecordPreferences>;
}

export default MyCommissionsReport;
